import { useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Icon } from '../icons/Icon.js';
import { cx } from '../utils/cx.js';
import { useFieldContext } from './FormField.js';

/**
 * C-04 入力欄 / C-05 選択欄 / C-06 チェック。
 * 地は bg/sunken、枠は border/strong、エラーは state/danger の枠 + 文言（FormField 側）。
 */
export const inputBase =
  'w-full min-h-12 rounded-input border border-line-strong bg-card px-4 text-body text-fg placeholder:text-fg-tertiary ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:border-2 disabled:disabled-look';

function useFieldProps<T extends { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling'; required?: boolean }>(props: T) {
  const ctx = useFieldContext();
  if (!ctx) return props;
  return {
    ...props,
    id: props.id ?? ctx.id,
    'aria-describedby': props['aria-describedby'] ?? ctx.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (ctx.invalid || undefined),
    required: props.required ?? ctx.required,
  };
}

export interface InputProps extends ComponentPropsWithoutRef<'input'> {
  /** 数字は等幅にする（金額・件数・日付） */
  numeric?: boolean;
}

export function Input({ className, numeric, ...rest }: InputProps) {
  const p = useFieldProps(rest);
  return <input className={cx(inputBase, numeric && 'tabular', className)} {...p} />;
}

export interface TextareaProps extends ComponentPropsWithoutRef<'textarea'> {
  rows?: number;
}

export function Textarea({ className, rows = 3, ...rest }: TextareaProps) {
  const p = useFieldProps(rest);
  return <textarea rows={rows} className={cx(inputBase, 'py-2.5 leading-7', className)} {...p} />;
}

export interface SelectProps extends ComponentPropsWithoutRef<'select'> {
  placeholder?: string;
}

export function Select({ className, children, placeholder, ...rest }: SelectProps) {
  const p = useFieldProps(rest);
  return (
    <span className="relative block">
      <select className={cx(inputBase, 'appearance-none pr-12', className)} {...p}>
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {children}
      </select>
      <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-fg-secondary">
        <Icon name="expand_more" size={20} />
      </span>
    </span>
  );
}

/** 日付は OS / ブラウザ標準のピッカー（C-24）。値は YYYY-MM-DD */
export function DateInput({ className, ...rest }: Omit<InputProps, 'type'>) {
  return <Input type="date" className={cx('tabular', className)} {...rest} />;
}

export interface NumberInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  value: string;
  onChange: (value: string) => void;
  allowDecimal?: boolean;
  allowNegative?: boolean;
}

/** 数値（文字列で持つ。NUMERIC は文字列のまま送る） */
export function NumberInput({ value, onChange, allowDecimal = true, allowNegative = false, className, ...rest }: NumberInputProps) {
  return (
    <Input
      type="text"
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      numeric
      value={value}
      onChange={(e) => {
        let v = e.target.value.replace(/[０-９．－]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/,/g, '');
        const re = allowNegative ? (allowDecimal ? /[^0-9.-]/g : /[^0-9-]/g) : allowDecimal ? /[^0-9.]/g : /[^0-9]/g;
        v = v.replace(re, '');
        onChange(v);
      }}
      className={className}
      {...rest}
    />
  );
}

export interface MoneyInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  value: string;
  onChange: (value: string) => void;
  allowNegative?: boolean;
}

function groupDigits(raw: string): string {
  if (!raw) return '';
  const neg = raw.startsWith('-');
  const digits = raw.replace(/[^0-9]/g, '');
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + grouped;
}

/** 金額（円・整数）。¥ を前置し 3 桁区切りで表示、値は数字だけの文字列 */
export function MoneyInput({ value, onChange, allowNegative = false, className, ...rest }: MoneyInputProps) {
  return (
    <span className="relative block">
      <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-body text-fg-secondary tabular" aria-hidden="true">
        ¥
      </span>
      <Input
        type="text"
        inputMode="numeric"
        numeric
        value={groupDigits(value)}
        onChange={(e) => {
          let v = e.target.value.replace(/[０-９－]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/[¥,\s]/g, '');
          v = v.replace(allowNegative ? /[^0-9-]/g : /[^0-9]/g, '');
          if (allowNegative) v = v.replace(/(?!^)-/g, '');
          onChange(v);
        }}
        className={cx('pl-9 text-right', className)}
        {...rest}
      />
    </span>
  );
}

export interface CheckboxProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'children'> {
  label: ReactNode;
  description?: ReactNode;
}

export function Checkbox({ label, description, className, id: givenId, ...rest }: CheckboxProps) {
  const autoId = useId();
  const id = givenId ?? `c${autoId}`;
  return (
    <label htmlFor={id} className={cx('flex min-tap cursor-pointer items-start gap-3 py-2', rest.disabled && 'cursor-not-allowed', className)}>
      <span className="relative mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center">
        <input id={id} type="checkbox" className="peer h-6 w-6 cursor-pointer appearance-none rounded-[6px] border-2 border-line-strong bg-card checked:border-brand checked:bg-brand disabled:disabled-look" {...rest} />
        <span className="pointer-events-none absolute text-brand-on opacity-0 peer-checked:opacity-100">
          <Icon name="check" size={20} />
        </span>
      </span>
      <span className="flex flex-col">
        <span className="text-body">{label}</span>
        {description ? <span className="text-caption text-fg-secondary">{description}</span> : null}
      </span>
    </label>
  );
}

export interface SegmentedOption<V extends string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

export interface SegmentedProps<V extends string> {
  value: V;
  onChange: (v: V) => void;
  options: ReadonlyArray<SegmentedOption<V>>;
  'aria-label'?: string;
  className?: string;
}

/** 2〜4 択（選択中 = brand の塗り。色は使わない） */
export function Segmented<V extends string>({ value, onChange, options, className, ...rest }: SegmentedProps<V>) {
  return (
    <div role="radiogroup" aria-label={rest['aria-label']} className={cx('inline-flex flex-wrap gap-2', className)}>
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={cx(
              'min-tap rounded-pill border px-4 text-callout font-medium transition-colors disabled:disabled-look',
              selected ? 'border-brand bg-brand text-brand-on' : 'border-line-strong bg-card text-fg hover:bg-card',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export interface SearchInputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'value' | 'onChange' | 'type'> {
  value: string;
  onChange: (v: string) => void;
}

/** C-19 検索欄（ピル型・クリアはアイコンのみ可の例外 #4） */
export function SearchInput({ value, onChange, className, placeholder = '検索', ...rest }: SearchInputProps) {
  return (
    <span className={cx('relative block', className)}>
      <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-fg-secondary">
        <Icon name="search" size={20} />
      </span>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cx(inputBase, 'rounded-pill pl-12 pr-12 [&::-webkit-search-cancel-button]:hidden')}
        {...rest}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="検索をクリア"
          className="absolute inset-y-0 right-1 my-auto flex h-10 w-10 items-center justify-center rounded-pill text-fg-secondary hover:bg-card"
        >
          <Icon name="close" size={20} />
        </button>
      ) : null}
    </span>
  );
}
